import React, {
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useStore } from 'effector-react';
import { useHoverDirty } from 'react-use';
import useConstant from 'use-constant';
import VipWrapper from '@components/VipWrapper';
import IconArrow from '../icons/IconArrow';
import CollapseContext from './CollapseContext';
import { $collapse, $collapseApi } from './collapseStore';

interface Props {
  isHideDefault?: boolean;
  className?: string;
  title: ReactNode;
  style?: CSSProperties;
  showVip?: boolean;
  children: ReactNode;
}

function Collapse({
  isHideDefault = false,
  className,
  title,
  style,
  showVip,
  children,
}: Props) {
  const id = useConstant(() => Symbol('collapseItem'));
  const { add, remove } = useContext(CollapseContext);
  const openIds = useStore($collapse);
  const ref = useRef(null);
  const isHovered = useHoverDirty(ref);

  useEffect(() => {
    $collapseApi.toggle({ id, isOn: !isHideDefault });
    add(id);
    return () => {
      remove(id);
    };
  }, []);

  const isOn = openIds.has(id);

  return (
    <div
      className={classNames('mb-10 rounded bg-black-#28 p-15', className)}
      style={style}
    >
      <div
        className="flex cursor-pointer select-none items-center justify-between"
        ref={ref}
        onClick={() => {
          $collapseApi.toggle({ id, isOn: !isOn });
        }}
      >
        <div className="flex-direction flex">
          <VipWrapper showVip={showVip}>
            <div>{title}</div>
          </VipWrapper>
        </div>
        <IconArrow
          active={isHovered}
          className={classNames({ 'rotate-180 transform': !isOn })}
        />
      </div>
      <div className={classNames('mt-20', { hidden: !isOn })}>{children}</div>
    </div>
  );
}

export default Collapse;
